/* Quick view for "Choose options" on product cards: loads /products/<handle>?view=dnquick
   (templates/product.dnquick.liquid) into a native dialog instead of the theme's bulk modal.
   The sibling switch inside it loads the sibling's quick view in place; sizes and quantities end in
   Request a quote (the configuration and quantities travel as ?config=, read by dnova-quote-prefill.js)
   or Add to cart. Without JavaScript the theme's own button and modal stay as they are. */
(() => {
  if (window.dnovaQuick) return;
  window.dnovaQuick = true;

  let dialog;
  let lastOpener = null;
  const ensureDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'dnq-dialog';
    dialog.setAttribute('aria-labelledby', 'dnq-title');
    dialog.innerHTML = '<button type="button" class="dnq-dialog__close" aria-label="Close" data-dnq-close>'
      + '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m4 4 8 8m0-8-8 8"/></svg></button>'
      + '<div class="dnq-dialog__body" data-dnq-body></div>';
    document.body.append(dialog);
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-dnq-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('dnq-open');
      if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
    });
    return dialog;
  };

  const money = (cents, format) => {
    const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (format || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  const load = async (url) => {
    const d = ensureDialog();
    const body = d.querySelector('[data-dnq-body]');
    body.classList.add('is-loading');
    if (!d.open) { d.showModal(); document.documentElement.classList.add('dnq-open'); }
    try {
      const u = new URL(url, location.origin);
      u.search = ''; u.searchParams.set('view', 'dnquick');
      const html = await (await fetch(u, { credentials: 'same-origin' })).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const view = doc.querySelector('[data-dnq]');
      if (!view) throw new Error('no quick view');
      body.replaceChildren(view);
      wire(view);
      body.scrollTop = 0;
    } catch (_) {
      // the quick view failed: go to the product page rather than leave an empty dialog
      d.close();
      location.href = url;
    } finally {
      body.classList.remove('is-loading');
    }
  };

  const wire = (view) => {
    const format = view.dataset.money;
    const rows = [...view.querySelectorAll('[data-dnq-row]')];
    const sum = view.querySelector('[data-dnq-sum]');
    const quote = view.querySelector('[data-dnq-quote]');
    const cart = view.querySelector('[data-dnq-cart]');
    const status = view.querySelector('[data-dnq-status]');

    const update = () => {
      let units = 0, total = 0, unpriced = 0;
      const lines = [];
      for (const row of rows) {
        const input = row.querySelector('[data-dnq-qty]');
        const q = Math.max(0, Math.floor(Number(input.value) || 0));
        if (String(q) !== input.value) input.value = q;
        const price = Number(row.dataset.price) || 0;
        row.classList.toggle('is-picked', q > 0);
        row.querySelector('[data-dnq-line]').textContent = q ? (price ? money(price * q, format) : 'On request') : '—';
        if (!q) continue;
        units += q; total += price * q; if (!price) unpriced += q;
        lines.push(`${row.dataset.label}${row.dataset.sku ? ` (SKU ${row.dataset.sku})` : ''} × ${q}`);
      }
      sum.textContent = units
        ? `${units} valve${units === 1 ? '' : 's'} · ${money(total, format)}${unpriced ? ` + ${unpriced} on request` : ''}`
        : 'Choose a quantity for each size you need.';
      cart.disabled = !units || unpriced > 0;
      cart.title = unpriced ? 'Sizes without a price can only be quoted' : '';
      const config = [view.dataset.title, ...lines].join('\n');
      quote.href = `${view.dataset.quote}?config=${encodeURIComponent(config)}`;
    };

    view.addEventListener('click', (e) => {
      const step = e.target.closest('[data-dnq-step]');
      if (step) {
        const input = step.parentElement.querySelector('[data-dnq-qty]');
        input.value = Math.max(0, (Number(input.value) || 0) + Number(step.dataset.dnqStep));
        update();
        return;
      }
      const thumb = e.target.closest('[data-dnq-thumb]');
      if (thumb) {
        // image_tag drops custom data attributes, so the photo is found by its box
        const main = view.querySelector('.dnq__main img');
        if (main) { main.removeAttribute('srcset'); main.removeAttribute('sizes'); main.src = thumb.dataset.dnqThumb; }
        view.querySelectorAll('[data-dnq-thumb]').forEach((t) => t.setAttribute('aria-pressed', t === thumb));
        return;
      }
      // the sibling switch: load the other product's quick view here instead of leaving the category
      const sib = e.target.closest('[data-dn-sib-link]');
      if (sib && !e.metaKey && !e.ctrlKey && !e.shiftKey) { e.preventDefault(); load(sib.href); }
    });
    view.addEventListener('input', (e) => { if (e.target.matches('[data-dnq-qty]')) update(); });

    cart.addEventListener('click', async () => {
      const items = rows.map((r) => ({ id: Number(r.dataset.id), quantity: Number(r.querySelector('[data-dnq-qty]').value) || 0 })).filter((i) => i.quantity > 0);
      if (!items.length) return;
      cart.disabled = true; status.textContent = 'Adding…';
      try {
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        const n = items.reduce((s, i) => s + i.quantity, 0);
        status.innerHTML = '';
        status.append(`${n} added to your cart. `);
        const a = document.createElement('a'); a.href = (window.Shopify?.routes?.root || '/') + 'cart'; a.textContent = 'View cart';
        status.append(a);
        refreshCartIcon();
      } catch (err) {
        status.textContent = `${err.message}. Try again, or request a quote instead.`;
      } finally {
        update();
      }
    });
    update();
  };

  // the header's cart count, the way the theme itself refreshes it
  const refreshCartIcon = async () => {
    try {
      const res = await fetch(`${location.pathname}?sections=cart-icon-bubble`);
      const html = (await res.json())['cart-icon-bubble'];
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      const icon = document.getElementById('cart-icon-bubble');
      if (fresh && icon) icon.innerHTML = fresh.innerHTML;
    } catch (_) { /* the count catches up on the next page */ }
  };

  // Take over the card's "Choose options" before the theme's modal opener sees the click. On a phone
  // a dialog this dense is worse than the product page itself, so the button simply goes there.
  const phone = window.matchMedia('(max-width: 749px)');
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.card-wrapper modal-opener button[data-product-url], .card-wrapper button.quick-add__submit[data-product-url]');
    if (!btn || e.metaKey || e.ctrlKey || e.shiftKey || typeof HTMLDialogElement !== 'function') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (phone.matches) { location.href = btn.dataset.productUrl; return; }
    lastOpener = btn;
    load(btn.dataset.productUrl);
  }, true);
})();
